import { IOrderRevisionLogEntry } from '../store/order/orderStore';
import { buildProductImageUrl } from './productImageUrl';

export const targetTypeLabel = (targetType: string) => {
    switch (targetType) {
        case 'LINE':
            return 'Line';
        case 'ADDRESS':
            return 'Address';
        case 'SHIPPING':
            return 'Shipping';
        case 'ORDER':
            return 'Order';
        default:
            return targetType;
    }
};

export interface ActivityLineDetail {
    lineId: number | null;
    label: string;
    quantity: number | null;
    productId?: string;
    seasonCode?: string;
    masterId?: string;
    colorCode?: string;
    colorName?: string;
}

type LineMeta = {
    lineId?: number;
    productId?: string;
    productName?: string;
    sizeName?: string;
    sizeCode?: string;
    colorName?: string;
    seasonCode?: string;
    masterId?: string;
    colorCode?: string;
    quantity?: number;
};

const mapLineMeta = (line: LineMeta, entry?: IOrderRevisionLogEntry): ActivityLineDetail => ({
    lineId: line.lineId != null ? line.lineId : (entry ? resolveLineId(entry) : null),
    label: formatLineMetaLabel(line),
    quantity: line.quantity != null ? line.quantity : null,
    productId: line.productId,
    seasonCode: line.seasonCode,
    masterId: line.masterId,
    colorCode: line.colorCode,
    colorName: line.colorName,
});

export const resolveActivityLineThumbnailUrl = (line: ActivityLineDetail): string | null => {
    const seasonCode = line.seasonCode && line.seasonCode.trim();
    const masterId = line.masterId && line.masterId.trim();
    const colorCode = line.colorCode && line.colorCode.trim();

    if (!seasonCode || !masterId || !colorCode) {
        return null;
    }

    return buildProductImageUrl(seasonCode, masterId, colorCode, 'thumb');
};

const formatLineMetaLabel = (meta: LineMeta) => {
    const parts: string[] = [];

    if (meta.productName && meta.productName.trim()) {
        parts.push(meta.productName.trim());
    } else if (meta.productId && meta.productId.trim()) {
        parts.push(meta.productId.trim());
    }

    if (meta.sizeName && meta.sizeName.trim()) {
        parts.push(meta.sizeName.trim());
    } else if (meta.sizeCode && meta.sizeCode.trim()) {
        parts.push(meta.sizeCode.trim());
    }

    if (parts.length === 0 && meta.productId) {
        return meta.productId;
    }

    return parts.join(' · ');
};

export const resolveLineId = (entry: IOrderRevisionLogEntry): number | null => {
    if (entry.metadata && entry.metadata.lineId) {
        return entry.metadata.lineId;
    }

    const match = entry.targetKey && entry.targetKey.match(/^line:(\d+)$/);
    if (match) {
        return +match[1];
    }

    return null;
};

export const getActivityLineDetails = (entry: IOrderRevisionLogEntry): ActivityLineDetail[] => {
    const meta = entry.metadata;
    if (!meta) {
        return [];
    }

    if (meta.lines && meta.lines.length) {
        return meta.lines.map((line) => mapLineMeta(line));
    }

    if (meta.lineId || meta.productName || meta.productId) {
        return [mapLineMeta(meta, entry)];
    }

    return [];
};

/** Login / email — identifies the user within the company. */
export const getActorPrimary = (entry: IOrderRevisionLogEntry) => {
    const first = entry.changedByFirstName && entry.changedByFirstName.trim();
    const last = entry.changedByLastName && entry.changedByLastName.trim();
    const fullName = [first, last].filter(Boolean).join(' ').trim();
    const login = entry.changedByLogin && entry.changedByLogin.trim();

    if (fullName && login) {
        return `${fullName} (${login})`;
    }

    if (login) {
        return login;
    }

    const displayName = entry.changedByDisplayName && entry.changedByDisplayName.trim();
    return displayName || fullName || 'User';
};

/** Customer / company name — secondary, often shared across users on the same order. */
export const getActorSecondary = (entry: IOrderRevisionLogEntry): string | null => {
    const login = entry.changedByLogin && entry.changedByLogin.trim();
    const displayName = entry.changedByDisplayName && entry.changedByDisplayName.trim();

    if (!displayName) {
        return null;
    }

    if (!login) {
        return null;
    }

    if (displayName === login) {
        return null;
    }

    return displayName;
};

export const getActorInitial = (entry: IOrderRevisionLogEntry) => {
    const primary = getActorPrimary(entry);
    const label = primary.includes('@') ? primary.split('@')[0] : primary;
    return label.charAt(0).toUpperCase();
};

export const getActivitySummary = (entry: IOrderRevisionLogEntry) => {
    const lineDetails = getActivityLineDetails(entry);
    if (lineDetails.length === 1) {
        const line = lineDetails[0];
        const qtySuffix = line.quantity != null && line.quantity > 0 ? ` (qty ${line.quantity})` : '';
        return entry.summary || `${line.label}${qtySuffix}`;
    }

    return entry.summary || `${getActorPrimary(entry)} updated the order`;
};

/** @deprecated Use getActorPrimary */
export const getActorName = (entry: IOrderRevisionLogEntry) => getActorPrimary(entry);

export const isEntryFromOtherUser = (entry: IOrderRevisionLogEntry, userLogin: string | null): boolean => {
    const actor = (entry.changedByLogin || '').trim().toLowerCase();
    const self = (userLogin || '').trim().toLowerCase();
    if (!actor) {
        return true;
    }
    if (!self) {
        return true;
    }
    return actor !== self;
};

export const hasOtherUserActivity = (entries: IOrderRevisionLogEntry[], userLogin: string | null): boolean =>
    entries.some((entry) => isEntryFromOtherUser(entry, userLogin));

export interface ActivityContributor {
    login: string;
    label: string;
}

export const entryMatchesContributor = (
    entry: IOrderRevisionLogEntry,
    contributorLogin: string
): boolean =>
    (entry.changedByLogin || '').trim().toLowerCase() === contributorLogin.trim().toLowerCase();

export const getDistinctContributors = (entries: IOrderRevisionLogEntry[]): ActivityContributor[] => {
    const seen = new Set<string>();
    const contributors: ActivityContributor[] = [];

    for (const entry of entries) {
        const login = (entry.changedByLogin || '').trim();
        if (!login) {
            continue;
        }

        const key = login.toLowerCase();
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        contributors.push({
            login,
            label: getActorPrimary(entry),
        });
    }

    return contributors;
};
